// src/components/SlideDeck.js
//
// RULING 311 (Bob 2026-10-13): the records become slides. One viewer for every deck - full screen, one slide at a time,
// arrow keys / Page keys / space to move, Escape to close. A deck is built from its record when it is opened
// (src\data\slideDecks.js) and never stored, so it cannot disagree with the record.
//
// A deck: { title, slides: [{ kicker, title, lines: [{ text, quote, cite, tone }], note }] }.
//   text  - a line of the record's own words or a field's value
//   quote - the agreement's (or a document's) own words, shown as a quotation
//   cite  - where it comes from (article, page, line)
//   tone  - 'warn' for what the record does not show, 'bad' for a flag
import React, { useCallback, useEffect, useState } from 'react';
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { bg: '#0B1626', card: '#FBFBF8', ink: '#14213D', text: '#3D4756', dim: '#5F6B7A', accent: '#9A4F12', rule: '#C9A35A', bad: '#8B1E1E', warn: '#9A6A12' };
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "'Segoe UI', Arial, sans-serif";

export function SlidesButton({ build, label, style, disabled }) {
  const [deck, setDeck] = useState(null);
  const [err, setErr] = useState('');
  const open = async () => {
    setErr('');
    try {
      const d = await build();
      if (!d || !Array.isArray(d.slides) || !d.slides.length) { setErr('There is nothing on the record to show yet.'); return; }
      setDeck(d);
    } catch (e) { setErr('The slides could not be built (' + (e.message || e) + ').'); }
  };
  return (
    <>
      <button type="button" onClick={open} disabled={disabled} title="Ruling 311: this record as slides"
        style={{ background: 'transparent', color: '#E8A020', border: '1px solid #E8A020', borderRadius: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', ...(style || {}) }}>
        {label || '▶ View as slides'}
      </button>
      {err && <span style={{ fontSize: 11, color: '#E06C6C', marginLeft: 8 }}>{err}</span>}
      {deck && <SlideDeck deck={deck} onClose={() => setDeck(null)} />}
    </>
  );
}

export default function SlideDeck({ deck, onClose }) {
  const slides = (deck && deck.slides) || [];
  const [i, setI] = useState(0);
  const n = slides.length;
  const go = useCallback((d) => setI((x) => Math.max(0, Math.min(n - 1, x + d))), [n]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      else if (['ArrowRight', 'PageDown', ' ', 'Enter'].includes(e.key)) { e.preventDefault(); go(1); }
      else if (['ArrowLeft', 'PageUp', 'Backspace'].includes(e.key)) { e.preventDefault(); go(-1); }
      else if (e.key === 'Home') setI(0);
      else if (e.key === 'End') setI(n - 1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [go, n, onClose]);
  if (!n) return null;
  const s = slides[i] || {};
  const tone = (t) => (t === 'bad' ? C.bad : t === 'warn' ? C.warn : C.text);
  return (
    <div role="dialog" aria-modal="true" aria-label={deck.title} style={{ position: 'fixed', inset: 0, zIndex: 10000, background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box' }}>
      <div style={{ width: 'min(100%, calc((100vh - 90px) * 16 / 9))', aspectRatio: '16 / 9', background: C.card, borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column', containerType: 'inline-size' }}
        {...activate(() => go(1))}>
        <div style={{ padding: '4cqw 5cqw 0', display: 'flex', flexDirection: 'column', gap: '1.2cqw', flex: 1, minHeight: 0, overflow: 'auto' }}>
          {s.kicker && <div style={{ fontFamily: SANS, fontSize: '1.3cqw', fontWeight: 700, letterSpacing: '.15cqw', textTransform: 'uppercase', color: C.accent }}>{s.kicker}</div>}
          {s.title && <div style={{ fontFamily: SERIF, fontSize: i === 0 ? '4.2cqw' : '2.9cqw', fontWeight: 600, lineHeight: 1.12, color: C.ink }}>{s.title}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1cqw', marginTop: '.6cqw' }}>
            {(s.lines || []).map((l, k) => (
              <div key={k}>
                {l.text && <div style={{ fontFamily: SANS, fontSize: '1.6cqw', lineHeight: 1.4, color: tone(l.tone), fontWeight: l.tone ? 600 : 400, whiteSpace: 'pre-wrap' }}>{l.text}</div>}
                {l.quote && <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '1.6cqw', lineHeight: 1.4, color: C.ink, borderLeft: '.35cqw solid ' + C.rule, paddingLeft: '1.2cqw', marginTop: '.4cqw', whiteSpace: 'pre-wrap' }}>“{l.quote}”</div>}
                {l.cite && <div style={{ fontFamily: SANS, fontSize: '1.15cqw', color: C.dim, marginTop: '.3cqw' }}>{l.cite}</div>}
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '1.2cqw 5cqw 2cqw', fontFamily: SANS, fontSize: '1.1cqw', color: C.dim, display: 'flex', justifyContent: 'space-between', gap: '2cqw' }}>
          <span>{deck.title}{s.note ? ' · ' + s.note : ''}</span>
          <span>{i + 1} / {n}</span>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button type="button" onClick={() => go(-1)} disabled={i === 0} style={navBtn}>‹ Back</button>
        <button type="button" onClick={() => go(1)} disabled={i >= n - 1} style={navBtn}>Next ›</button>
        <button type="button" onClick={onClose} style={{ ...navBtn, borderColor: '#E06C6C', color: '#E06C6C' }}>Close (Esc)</button>
      </div>
    </div>
  );
}
const navBtn = { background: 'transparent', color: '#DCE7F2', border: '1px solid #2E4A6B', borderRadius: 4, padding: '6px 14px', fontSize: 13, cursor: 'pointer' };
